import React from 'react';
import { FillPage } from '../../layout/FillPage';
import NotFound from '../../pages/OtherPage/NotFound';
import { Button, Callout, Skeleton, Workspace } from '../../ui/v2';
import { EventsScopeProvider } from './EventsScopeContext';
import { useEventsOrganizations } from './api/queries';

type EventsGateProps = {
  children: React.ReactNode;
};

/**
 * Admits users with an events role in at least one organization, per
 * GET /api/events/organizations/. Disabled organizations stay listed so
 * directors can reach reconciliation (addendum A5); the session decides
 * what each scope may do.
 */
export default function EventsGate({ children }: EventsGateProps) {
  const organizations = useEventsOrganizations();

  if (organizations.isPending) {
    return (
      <FillPage>
        <Workspace title="Events">
          <div
            aria-busy="true"
            className="flex flex-col items-start p-4 lg:p-6"
          >
            <p className="sr-only">Loading events…</p>
            <Skeleton className="h-4" width="28%" />
            <Skeleton className="mt-4 h-3" width="64%" />
          </div>
        </Workspace>
      </FillPage>
    );
  }

  // Only a failed first load is fatal. A failed background refetch keeps the
  // cached data, and unmounting here would drop the scope selection.
  if (organizations.data === undefined) {
    return (
      <FillPage>
        <Workspace title="Events">
          <div className="max-w-[640px] p-4 lg:p-6">
            <Callout tone="danger" title="Events couldn't load">
              Check your connection and try again.
              <div className="mt-2">
                <Button size="sm" onClick={() => void organizations.refetch()}>
                  Retry
                </Button>
              </div>
            </Callout>
          </div>
        </Workspace>
      </FillPage>
    );
  }

  if (organizations.data.length === 0) {
    return <NotFound />;
  }

  return (
    <EventsScopeProvider organizations={organizations.data}>
      {children}
    </EventsScopeProvider>
  );
}
